/* oxlint-disable no-instanceof/no-instanceof */
import { Runnable, RunnableConfig } from "@langchain/core/runnables";
import {
  BaseMessage,
  AIMessage,
  ToolMessage,
  SystemMessage,
} from "@langchain/core/messages";
import {
  Command,
  isCommand,
  type LangGraphRunnableConfig,
} from "@langchain/langgraph";
import { type BaseChatModelCallOptions } from "@langchain/core/language_models/chat_models";
import {
  InteropZodObject,
  getSchemaDescription,
  interopParse,
} from "@langchain/core/utils/types";
import { raceWithSignal } from "@langchain/core/runnables";
import type { ToolCall } from "@langchain/core/messages/tool";
import type { ClientTool, ServerTool } from "@langchain/core/tools";

import { initChatModel } from "../../chat_models/universal.js";
import {
  MultipleStructuredOutputsError,
  MiddlewareError,
  StructuredOutputParsingError,
} from "../errors.js";
import { RunnableCallable } from "../RunnableCallable.js";
import type { AgentLanguageModelLike as LanguageModelLike } from "../model.js";
import {
  bindTools,
  validateLLMHasNoBoundTools,
  hasToolCalls,
  isClientTool,
} from "../utils.js";
import { isConfigurableModel } from "../model.js";
import { mergeAbortSignals, toPartialZodObject } from "../nodes/utils.js";
import { CreateAgentParams } from "../types.js";
import type { InternalAgentState, Runtime } from "../runtime.js";
import type {
  AgentMiddleware,
  AnyAnnotationRoot,
  WrapModelCallHandler,
} from "../middleware/types.js";
import type { ModelRequest } from "./types.js";
import { withAgentName } from "../withAgentName.js";
import {
  ToolStrategy,
  ProviderStrategy,
  transformResponseFormat,
  ToolStrategyError,
  type ResponseFormatInput,
} from "../responses.js";

type ResponseHandlerResult<StructuredResponseFormat> =
  | {
      structuredResponse: StructuredResponseFormat;
      messages: BaseMessage[];
    }
  | Promise<Command>;

/**
 * Wrap the base handler with middleware wrapModelCall hooks
 * Middleware are composed so the first middleware is the outermost wrapper
 * Example: [auth, retry, cache] means auth wraps retry wraps cache wraps baseHandler
 */
type InternalModelResponse<StructuredResponseFormat> =
  | AIMessage
  | ResponseHandlerResult<StructuredResponseFormat>
  | Command;

/**
 * Check if the response is an internal model response.
 * @param response - The response to check.
 * @returns True if the response is an internal model response, false otherwise.
 */
function isInternalModelResponse<StructuredResponseFormat>(
  response: unknown
): response is InternalModelResponse<StructuredResponseFormat> {
  return (
    AIMessage.isInstance(response) ||
    isCommand(response) ||
    (typeof response === "object" &&
      response !== null &&
      "structuredResponse" in response &&
      "messages" in response)
  );
}

/**
 * The name of the agent node in the state graph.
 */
export const AGENT_NODE_NAME = "model_request";

export interface AgentNodeOptions<
  StructuredResponseFormat extends Record<string, unknown> = Record<
    string,
    unknown
  >,
  StateSchema extends AnyAnnotationRoot | InteropZodObject = AnyAnnotationRoot,
  ContextSchema extends AnyAnnotationRoot | InteropZodObject =
    AnyAnnotationRoot,
> extends Pick<
  CreateAgentParams<StructuredResponseFormat, StateSchema, ContextSchema>,
  "model" | "includeAgentName" | "name" | "responseFormat" | "middleware"
> {
  toolClasses: (ClientTool | ServerTool)[];
  shouldReturnDirect: Set<string>;
  signal?: AbortSignal;
  systemMessage: SystemMessage;
  wrapModelCallHookMiddleware?: (
    | AgentMiddleware
    | [AgentMiddleware, (...args: unknown[]) => Record<string, unknown>]
  )[];
}

interface NativeResponseFormat {
  type: "native";
  strategy: ProviderStrategy;
}

interface ToolResponseFormat {
  type: "tool";
  tools: Record<string, ToolStrategy>;
}

type ResponseFormat = NativeResponseFormat | ToolResponseFormat;

export class AgentNode<
  StructuredResponseFormat extends Record<string, unknown> = Record<
    string,
    unknown
  >,
  ContextSchema extends AnyAnnotationRoot | InteropZodObject =
    AnyAnnotationRoot,
> extends RunnableCallable<
  InternalAgentState<StructuredResponseFormat>,
  | Command[]
  | {
      messages: BaseMessage[];
      structuredResponse: StructuredResponseFormat;
    }
> {
  #options: AgentNodeOptions<StructuredResponseFormat, ContextSchema>;
  #systemMessage: SystemMessage;

  constructor(
    options: AgentNodeOptions<StructuredResponseFormat, ContextSchema>
  ) {
    super({
      name: options.name ?? "model",
      func: (input, config) => this.#run(input, config as RunnableConfig),
    });

    this.#options = options;
    this.#systemMessage = options.systemMessage;
  }

  /**
   * Returns response format primtivies based on given model and response format provided by the user.
   *
   * If the user selects a tool output:
   * - return a record of tools to extract structured output from the model's response
   *
   * if the user selects a native schema output or if the model supports JSON schema output:
   * - return a provider strategy to extract structured output from the model's response
   *
   * @param model - The model to get the response format for.
   * @returns The response format.
   */
  async #getResponseFormat(
    model: string | LanguageModelLike,
    responseFormat: ResponseFormatInput | undefined = this.#options
      .responseFormat
  ): Promise<ResponseFormat | undefined> {
    if (!responseFormat) {
      return undefined;
    }

    let resolvedModel: LanguageModelLike | undefined;
    if (isConfigurableModel(model)) {
      resolvedModel = await (
        model as unknown as {
          _getModelInstance: () => Promise<LanguageModelLike>;
        }
      )._getModelInstance();
    } else if (typeof model !== "string") {
      resolvedModel = model;
    }

    const strategies = transformResponseFormat(
      responseFormat,
      undefined,
      resolvedModel
    );

    if (strategies.length === 0) {
      return undefined;
    }

    /**
     * we either define a list of provider strategies or a list of tool strategies
     */
    const isProviderStrategy = strategies.every(
      (format) => format instanceof ProviderStrategy
    );

    /**
     * Populate a list of structured tool info.
     */
    if (!isProviderStrategy) {
      return {
        type: "tool",
        tools: (
          strategies.filter(
            (format) => format instanceof ToolStrategy
          ) as ToolStrategy[]
        ).reduce(
          (acc, format) => {
            acc[format.name] = format;
            return acc;
          },
          {} as Record<string, ToolStrategy>
        ),
      };
    }

    return {
      type: "native",
      /**
       * there can only be one provider strategy
       */
      strategy: strategies[0] as ProviderStrategy,
    };
  }

  async #run(
    state: InternalAgentState<StructuredResponseFormat>,
    config: RunnableConfig
  ) {
    /**
     * Check if we just executed a returnDirect tool
     * If so, we should generate structured response (if needed) and stop
     */
    const lastMessage = state.messages.at(-1);
    if (
      lastMessage &&
      ToolMessage.isInstance(lastMessage) &&
      lastMessage.name &&
      this.#options.shouldReturnDirect.has(lastMessage.name)
    ) {
      return [new Command({ update: { messages: [] } })];
    }

    const { response, lastAiMessage, collectedCommands } =
      await this.#invokeModel(state, config);

    /**
     * structuredResponse — return as a plain state update dict (not a Command)
     * because the structuredResponse channel uses UntrackedValue(guard=true)
     * which only allows a single write per step.
     */
    if (
      typeof response === "object" &&
      response !== null &&
      "structuredResponse" in response &&
      "messages" in response
    ) {
      const { structuredResponse, messages } = response as {
        structuredResponse: StructuredResponseFormat;
        messages: BaseMessage[];
      };
      return {
        messages: [...state.messages, ...messages],
        structuredResponse,
      };
    }

    const commands: Command[] = [];
    const aiMessage: AIMessage | null = AIMessage.isInstance(response)
      ? response
      : lastAiMessage;

    // messages
    if (aiMessage) {
      aiMessage.name = this.name;
      aiMessage.lc_kwargs.name = this.name;

      if (this.#areMoreStepsNeeded(state, aiMessage)) {
        commands.push(
          new Command({
            update: {
              messages: [
                new AIMessage({
                  content: "Sorry, need more steps to process this request.",
                  name: this.name,
                  id: aiMessage.id,
                }),
              ],
            },
          })
        );
      } else {
        commands.push(new Command({ update: { messages: [aiMessage] } }));
      }
    }

    // Commands (from base handler retries or middleware)
    if (isCommand(response) && !collectedCommands.includes(response)) {
      commands.push(response);
    }
    commands.push(...collectedCommands);

    return commands;
  }

  /**
   * Derive the model from the options.
   * @param state - The state of the agent.
   * @param config - The config of the agent.
   * @returns The model.
   */
  #deriveModel() {
    if (typeof this.#options.model === "string") {
      return initChatModel(this.#options.model);
    }

    if (this.#options.model) {
      return this.#options.model;
    }

    throw new Error("No model option was provided, either via `model` option.");
  }

  async #invokeModel(
    state: InternalAgentState<StructuredResponseFormat>,
    config: RunnableConfig,
    options: {
      lastMessage?: string;
    } = {}
  ): Promise<{
    response: InternalModelResponse<StructuredResponseFormat>;
    lastAiMessage: AIMessage | null;
    collectedCommands: Command[];
  }> {
    const model = await this.#deriveModel();
    const lgConfig = config as LangGraphRunnableConfig;

    /**
     * Create a local variable for current system message to avoid concurrency issues
     * Each invocation gets its own copy
     */
    let currentSystemMessage = this.#systemMessage;

    /**
     * Shared tracking state for AIMessage and Command collection.
     * lastAiMessage tracks the effective AIMessage through the middleware chain.
     * collectedCommands accumulates Commands returned by middleware (not base handler).
     */
    let lastAiMessage: AIMessage | null = null;
    const collectedCommands: Command[] = [];

    /**
     * Create the base handler that performs the actual model invocation
     */
    const baseHandler = async (
      request: ModelRequest
    ): Promise<AIMessage | ResponseHandlerResult<StructuredResponseFormat>> => {
      /**
       * Check if the LLM already has bound tools and throw if it does.
       */
      validateLLMHasNoBoundTools(request.model);

      const structuredResponseFormat = await this.#getResponseFormat(
        request.model,
        request.responseFormat
      );
      const modelWithTools = await this.#bindTools(
        request.model,
        request,
        structuredResponseFormat
      );

      /**
       * prepend the system message to the messages if it is not empty
       */
      const messages = [
        ...(currentSystemMessage.text === "" ? [] : [currentSystemMessage]),
        ...request.messages,
      ];

      const signal = mergeAbortSignals(this.#options.signal, config.signal);
      const response = (await raceWithSignal(
        modelWithTools.invoke(messages, {
          ...config,
          signal,
        }),
        signal
      )) as AIMessage;

      lastAiMessage = response;

      /**
       * if the user requests a native schema output, try to parse the response
       * and return the structured response if it is valid
       */
      if (structuredResponseFormat?.type === "native") {
        const structuredResponse =
          structuredResponseFormat.strategy.parse(response);
        if (structuredResponse) {
          return { structuredResponse, messages: [response] };
        }

        /**
         * If the model produced a terminal response (no tool calls) but the
         * output failed to satisfy the provider strategy's schema, throw an
         * informative error instead of silently exiting with
         * `structuredResponse: undefined`. If tool calls are present, the
         * agent loop continues and a subsequent terminal step will get
         * another chance to produce a valid structured response.
         */
        if (!response.tool_calls || response.tool_calls.length === 0) {
          const schemaTitle =
            typeof structuredResponseFormat.strategy.schema?.title === "string"
              ? structuredResponseFormat.strategy.schema.title
              : "providerStrategy";
          throw new StructuredOutputParsingError(schemaTitle, [
            "Model output did not satisfy the provided response schema.",
          ]);
        }

        return response;
      }

      if (!structuredResponseFormat || !response.tool_calls) {
        return response;
      }

      const toolCalls = response.tool_calls.filter(
        (call) => call.name in structuredResponseFormat.tools
      );

      /**
       * if there were not structured tool calls, we can return the response
       */
      if (toolCalls.length === 0) {
        return response;
      }

      /**
       * if there were multiple structured tool calls, we should throw an error as this
       * scenario is not defined/supported.
       */
      if (toolCalls.length > 1) {
        return this.#handleMultipleStructuredOutputs(
          response,
          toolCalls,
          structuredResponseFormat
        );
      }

      const toolStrategy = structuredResponseFormat.tools[toolCalls[0].name];
      const toolMessageContent = toolStrategy?.options?.toolMessageContent;
      return this.#handleSingleStructuredOutput(
        response,
        toolCalls[0],
        structuredResponseFormat,
        toolMessageContent ?? options.lastMessage
      );
    };

    const wrapperMiddleware = this.#options.wrapModelCallHookMiddleware ?? [];
    let wrappedHandler: (
      request: ModelRequest<
        InternalAgentState<StructuredResponseFormat>,
        unknown
      >
    ) => Promise<InternalModelResponse<StructuredResponseFormat>> = baseHandler;

    /**
     * Build composed handler from last to first so first middleware becomes outermost
     */
    for (let i = wrapperMiddleware.length - 1; i >= 0; i--) {
      const middlewareEntry = wrapperMiddleware[i];
      const middleware = Array.isArray(middlewareEntry)
        ? middlewareEntry[0]
        : middlewareEntry;
      if (middleware.wrapModelCall) {
        const innerHandler = wrappedHandler;
        const currentMiddleware = middleware;

        wrappedHandler = async (
          request: ModelRequest<
            InternalAgentState<StructuredResponseFormat>,
            unknown
          >
        ): Promise<InternalModelResponse<StructuredResponseFormat>> => {
          const baselineSystemMessage = currentSystemMessage;

          /**
           * Merge context with default context of middleware
           */
          const context = currentMiddleware.contextSchema
            ? interopParse(
                currentMiddleware.contextSchema,
                lgConfig?.context || {}
              )
            : lgConfig?.context;

          /**
           * Create runtime
           */
          const runtime: Runtime<unknown> = Object.freeze({
            context,
            store: lgConfig.store,
            configurable: lgConfig.configurable,
            writer: lgConfig.writer,
            interrupt: lgConfig.interrupt,
            signal: lgConfig.signal,
          });

          /**
           * Create the request with state and runtime
           */
          const requestWithStateAndRuntime: ModelRequest<
            InternalAgentState<StructuredResponseFormat>,
            unknown
          > = {
            ...request,
            state: {
              ...(middleware.stateSchema
                ? interopParse(
                    toPartialZodObject(middleware.stateSchema),
                    state
                  )
                : {}),
              messages: state.messages,
            } as InternalAgentState<StructuredResponseFormat>,
            runtime,
          };

          /**
           * Create handler that validates tools and calls the inner handler
           */
          const handlerWithValidation = async (
            req: ModelRequest<
              InternalAgentState<StructuredResponseFormat>,
              unknown
            >
          ): Promise<InternalModelResponse<StructuredResponseFormat>> => {
            currentSystemMessage = baselineSystemMessage;

            /**
             * Validate tool modifications in wrapModelCall.
             *
             * Classify each client tool as either:
             * - "added": a genuinely new tool name not in the static toolClasses
             * - "replaced": same name as a registered tool but different instance
             *
             * Added tools are allowed when a wrapToolCall middleware exists to
             * handle their execution. Replaced tools are always rejected to
             * preserve ToolNode execution identity.
             */
            const modifiedTools = req.tools ?? [];
            const registeredToolsByName = new Map(
              this.#options.toolClasses
                .filter(isClientTool)
                .map((t) => [t.name, t] as const)
            );

            const addedClientTools = modifiedTools.filter(
              (tool) =>
                isClientTool(tool) && !registeredToolsByName.has(tool.name)
            );

            const replacedClientTools = modifiedTools.filter((tool) => {
              if (!isClientTool(tool)) return false;
              const original = registeredToolsByName.get(tool.name);
              return original != null && original !== tool;
            });

            if (addedClientTools.length > 0) {
              const hasWrapToolCallHandler = this.#options.middleware?.some(
                (m) => m.wrapToolCall != null
              );
              if (!hasWrapToolCallHandler) {
                throw new Error(
                  `You have added a new tool in "wrapModelCall" hook of middleware "${
                    currentMiddleware.name
                  }": ${addedClientTools
                    .map((tool) => tool.name)
                    .join(
                      ", "
                    )}. This is not supported unless a middleware provides a "wrapToolCall" handler to execute it.`
                );
              }
            }

            if (replacedClientTools.length > 0) {
              throw new Error(
                `You have modified a tool in "wrapModelCall" hook of middleware "${
                  currentMiddleware.name
                }": ${replacedClientTools
                  .map((tool) => tool.name)
                  .join(", ")}. This is not supported.`
              );
            }

            let normalizedReq = req;
            const hasSystemPromptChanged =
              req.systemPrompt !== currentSystemMessage.text;
            const hasSystemMessageChanged =
              req.systemMessage !== currentSystemMessage;
            if (hasSystemPromptChanged && hasSystemMessageChanged) {
              throw new Error(
                "Cannot change both systemPrompt and systemMessage in the same request."
              );
            }

            /**
             * Check if systemPrompt is a string was changed, if so create a new SystemMessage
             */
            if (hasSystemPromptChanged) {
              currentSystemMessage = new SystemMessage({
                content: [{ type: "text", text: req.systemPrompt }],
              });
              normalizedReq = {
                ...req,
                systemPrompt: currentSystemMessage.text,
                systemMessage: currentSystemMessage,
              };
            }
            /**
             * If the systemMessage was changed, update the current system message
             */
            if (hasSystemMessageChanged) {
              currentSystemMessage = new SystemMessage({
                ...req.systemMessage,
              });
              normalizedReq = {
                ...req,
                systemPrompt: currentSystemMessage.text,
                systemMessage: currentSystemMessage,
              };
            }

            const innerHandlerResult = await innerHandler(normalizedReq);

            /**
             * Normalize Commands so middleware always sees AIMessage from handler().
             * When an inner handler (base handler or nested middleware) returns a
             * Command (e.g. structured-output retry), substitute the tracked
             * lastAiMessage so the middleware sees an AIMessage, and collect the
             * raw Command so the framework can still propagate it (e.g. for retries).
             *
             * Only collect if not already present: Commands from inner middleware
             * are already tracked via the middleware validation layer (line ~627).
             */
            if (isCommand(innerHandlerResult) && lastAiMessage) {
              if (!collectedCommands.includes(innerHandlerResult)) {
                collectedCommands.push(innerHandlerResult);
              }
              return lastAiMessage as InternalModelResponse<StructuredResponseFormat>;
            }

            return innerHandlerResult;
          };

          // Call middleware's wrapModelCall with the validation handler
          if (!currentMiddleware.wrapModelCall) {
            return handlerWithValidation(requestWithStateAndRuntime);
          }

          try {
            const middlewareResponse = await currentMiddleware.wrapModelCall(
              requestWithStateAndRuntime,
              handlerWithValidation as WrapModelCallHandler
            );

            /**
             * Validate that this specific middleware returned a valid response
             */
            if (!isInternalModelResponse(middlewareResponse)) {
              throw new Error(
                `Invalid response from "wrapModelCall" in middleware "${
                  currentMiddleware.name
                }": expected AIMessage or Command, got ${typeof middlewareResponse}`
              );
            }

            if (AIMessage.isInstance(middlewareResponse)) {
              lastAiMessage = middlewareResponse;
            } else if (isCommand(middlewareResponse)) {
              collectedCommands.push(middlewareResponse);
            }

            return middlewareResponse;
          } catch (error) {
            throw MiddlewareError.wrap(error, currentMiddleware.name);
          }
        };
      }
    }

    /**
     * Execute the wrapped handler with the initial request
     * Reset current system prompt to initial state and convert to string using .text getter
     * for backwards compatibility with ModelRequest
     */
    currentSystemMessage = this.#systemMessage;
    const initialRequest: ModelRequest<
      InternalAgentState<StructuredResponseFormat>,
      unknown
    > = {
      model,
      responseFormat: this.#options.responseFormat,
      systemPrompt: currentSystemMessage?.text,
      systemMessage: currentSystemMessage,
      messages: state.messages,
      tools: this.#options.toolClasses,
      state,
      runtime: Object.freeze({
        context: lgConfig?.context,
        store: lgConfig.store,
        configurable: lgConfig.configurable,
        writer: lgConfig.writer,
        interrupt: lgConfig.interrupt,
        signal: lgConfig.signal,
      }) as Runtime<unknown>,
    };

    const response = await wrappedHandler(initialRequest);
    return { response, lastAiMessage, collectedCommands };
  }

  /**
   * If the model returns multiple structured outputs, we need to handle it.
   * @param response - The response from the model
   * @param toolCalls - The tool calls that were made
   * @returns The response from the model
   */
  #handleMultipleStructuredOutputs(
    response: AIMessage,
    toolCalls: ToolCall[],
    responseFormat: ToolResponseFormat
  ): Promise<Command> {
    const multipleStructuredOutputsError = new MultipleStructuredOutputsError(
      toolCalls.map((call) => call.name)
    );

    return this.#handleToolStrategyError(
      multipleStructuredOutputsError,
      response,
      toolCalls[0],
      responseFormat
    );
  }

  /**
   * If the model returns a single structured output, we need to handle it.
   * @param toolCall - The tool call that was made
   * @returns The structured response and a message to the LLM if needed
   */
  #handleSingleStructuredOutput(
    response: AIMessage,
    toolCall: ToolCall,
    responseFormat: ToolResponseFormat,
    lastMessage?: string
  ): ResponseHandlerResult<StructuredResponseFormat> {
    const tool = responseFormat.tools[toolCall.name];

    try {
      const structuredResponse = tool.parse(
        toolCall.args
      ) as StructuredResponseFormat;

      return {
        structuredResponse,
        messages: [
          response,
          new ToolMessage({
            tool_call_id: toolCall.id ?? "",
            content: JSON.stringify(structuredResponse),
            name: toolCall.name,
          }),
          new AIMessage(
            lastMessage ??
              `Returning structured response: ${JSON.stringify(
                structuredResponse
              )}`
          ),
        ],
      };
    } catch (error) {
      return this.#handleToolStrategyError(
        error as ToolStrategyError,
        response,
        toolCall,
        responseFormat
      );
    }
  }

  async #handleToolStrategyError(
    error: ToolStrategyError,
    response: AIMessage,
    toolCall: ToolCall,
    responseFormat: ToolResponseFormat
  ): Promise<Command> {
    /**
     * Using the `errorHandler` option of the first `ToolStrategy` entry is sufficient here.
     * There is technically only one `ToolStrategy` entry in `structuredToolInfo` if the user
     * uses `toolStrategy` to define the response format. If the user applies a list of json
     * schema objects, these will be transformed into multiple `ToolStrategy` entries but all
     * with the same `handleError` option.
     */
    const errorHandler = Object.values(responseFormat.tools).at(0)?.options
      ?.handleError;

    const toolCallId = toolCall.id;
    if (!toolCallId) {
      throw new Error(
        "Tool call ID is required to handle tool output errors. Please provide a tool call ID."
      );
    }

    /**
     * Default behavior: retry if `errorHandler` is undefined or truthy.
     * Only throw if explicitly set to `false`.
     */
    if (errorHandler === false) {
      throw error;
    }

    /**
     * retry if:
     */
    if (
      /**
       * if the user has provided truthy value as the `errorHandler`, return a new AIMessage
       * with the error message and retry the tool call.
       */
      errorHandler === undefined ||
      (typeof errorHandler === "boolean" && errorHandler) ||
      /**
       * if `errorHandler` is an array and contains MultipleStructuredOutputsError
       */
      (Array.isArray(errorHandler) &&
        errorHandler.some((h) => h instanceof MultipleStructuredOutputsError))
    ) {
      return new Command({
        update: {
          messages: [
            response,
            new ToolMessage({
              content: error.message,
              tool_call_id: toolCallId,
            }),
          ],
        },
        goto: AGENT_NODE_NAME,
      });
    }

    /**
     * if `errorHandler` is a string, retry the tool call with given string
     */
    if (typeof errorHandler === "string") {
      return new Command({
        update: {
          messages: [
            response,
            new ToolMessage({
              content: errorHandler,
              tool_call_id: toolCallId,
            }),
          ],
        },
        goto: AGENT_NODE_NAME,
      });
    }

    /**
     * if `errorHandler` is a function, retry the tool call with the function
     */
    if (typeof errorHandler === "function") {
      const content = await errorHandler(error);
      if (typeof content !== "string") {
        throw new Error("Error handler must return a string.");
      }

      return new Command({
        update: {
          messages: [
            response,
            new ToolMessage({
              content,
              tool_call_id: toolCallId,
            }),
          ],
        },
        goto: AGENT_NODE_NAME,
      });
    }

    /**
     * Default: retry if we reach here
     */
    return new Command({
      update: {
        messages: [
          response,
          new ToolMessage({
            content: error.message,
            tool_call_id: toolCallId,
          }),
        ],
      },
      goto: AGENT_NODE_NAME,
    });
  }

  #areMoreStepsNeeded(
    state: InternalAgentState<StructuredResponseFormat>,
    response: BaseMessage
  ): boolean {
    const allToolsReturnDirect =
      AIMessage.isInstance(response) &&
      response.tool_calls?.every((call) =>
        this.#options.shouldReturnDirect.has(call.name)
      );
    const remainingSteps =
      "remainingSteps" in state ? (state.remainingSteps as number) : undefined;
    return Boolean(
      remainingSteps &&
      ((remainingSteps < 1 && allToolsReturnDirect) ||
        (remainingSteps < 2 && hasToolCalls(state.messages.at(-1))))
    );
  }

  async #bindTools(
    model: LanguageModelLike,
    preparedOptions: ModelRequest | undefined,
    structuredResponseFormat: ResponseFormat | undefined
  ): Promise<LanguageModelLike | Runnable> {
    const options: Partial<BaseChatModelCallOptions> = {};
    const structuredTools = Object.values(
      structuredResponseFormat && "tools" in structuredResponseFormat
        ? structuredResponseFormat.tools
        : {}
    );

    /**
     * Use tools from preparedOptions if provided, otherwise use default tools
     */
    const allTools = [
      ...(preparedOptions?.tools ?? this.#options.toolClasses),
      ...structuredTools.map((toolStrategy) => toolStrategy.tool),
    ];

    /**
     * If there are structured tools, we need to set the tool choice to "any"
     * so that the model can choose to use a structured tool or not.
     */
    const toolChoice =
      preparedOptions?.toolChoice ||
      (structuredTools.length > 0 ? "any" : undefined);

    /**
     * check if the user requests a native schema output
     */
    if (structuredResponseFormat?.type === "native") {
      const resolvedStrict =
        preparedOptions?.modelSettings?.strict ??
        structuredResponseFormat?.strategy?.strict ??
        true;

      const jsonSchemaParams = {
        name: structuredResponseFormat.strategy.schema?.name ?? "extract",
        description: getSchemaDescription(
          structuredResponseFormat.strategy.schema
        ),
        schema: structuredResponseFormat.strategy.schema,
        strict: resolvedStrict,
      };

      Object.assign(options, {
        /**
         * OpenAI-style options
         * Used by ChatOpenAI, ChatXAI, and other OpenAI-compatible providers.
         */
        response_format: {
          type: "json_schema",
          json_schema: jsonSchemaParams,
        },

        /**
         * Anthropic-style options
         */
        outputConfig: {
          format: {
            type: "json_schema",
            schema: structuredResponseFormat.strategy.schema,
          },
        },

        /**
         * Google-style options
         * Used by ChatGoogle and other Gemini-based providers.
         */
        responseSchema: structuredResponseFormat.strategy.schema,

        /**
         * for LangSmith structured output tracing
         */
        ls_structured_output_format: {
          kwargs: { method: "json_schema" },
          schema: structuredResponseFormat.strategy.schema,
        },
        strict: resolvedStrict,
      });
    }

    /**
     * Bind tools to the model if they are not already bound.
     */
    const modelWithTools = await bindTools(model, allTools, {
      ...options,
      ...preparedOptions?.modelSettings,
      tool_choice: toolChoice,
    });

    /**
     * Create a model runnable with the prompt and agent name
     * Use current SystemMessage state (which may have been modified by middleware)
     */
    const modelRunnable =
      this.#options.includeAgentName === "inline"
        ? withAgentName(modelWithTools, this.#options.includeAgentName)
        : modelWithTools;

    return modelRunnable;
  }

  /**
   * Returns internal bookkeeping state for StateManager, not graph output.
   * The return shape differs from the node's output type (Command).
   */
  // @ts-expect-error Internal state shape differs from graph output type
  getState(): { messages: BaseMessage[] } {
    const state = super.getState();
    const origState = state && !isCommand(state) ? state : {};

    return {
      messages: [],
      ...origState,
    };
  }
}
